import React, { useState, useEffect, useMemo } from 'react';
import { getCardgames } from '../services/supabaseService';

export interface CardGameItem {
  id: number | string;
  name: string;
  slug?: string;
  homura?: boolean;
  kyubey?: boolean;
  sauce?: string | null;
  card_back_url?: string;
  isAvailable?: boolean;
  apiBaseUrl?: string;
}

export interface GameSelectorProps {
  onSelectGame: (gameId: number | string, game?: CardGameItem) => void;
  selectedGameId?: number | string | 'All';
  games?: CardGameItem[];
  title?: string;
  subtitle?: string;
  showSearch?: boolean;
  className?: string;
}

export const GameSelector: React.FC<GameSelectorProps> = ({
  onSelectGame,
  selectedGameId,
  games: initialGames,
  title = 'Escolha um Jogo',
  subtitle = 'Para continuar, selecione uma franquia de TCG abaixo:',
  showSearch = true,
  className = ''
}) => {
  const [games, setGames] = useState<CardGameItem[]>(initialGames || []);
  const [loading, setLoading] = useState<boolean>(!initialGames || initialGames.length === 0);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (initialGames && initialGames.length > 0) {
      setGames(initialGames);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    getCardgames()
      .then((data) => {
        if (isMounted && Array.isArray(data)) {
          setGames(data);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar lista de jogos no GameSelector:', err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [initialGames]);

  const filteredGames = useMemo(() => {
    if (!searchQuery.trim()) return games;
    const q = searchQuery.toLowerCase().trim();
    return games.filter((g) => g.name?.toLowerCase().includes(q));
  }, [games, searchQuery]);

  return (
    <div className={`w-full max-w-6xl mx-auto py-8 sm:py-12 px-4 sm:px-6 space-y-8 animate-in fade-in zoom-in-95 duration-300 ${className}`}>
      {/* Header Container */}
      <div className="text-center space-y-3 max-w-2xl mx-auto">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white uppercase italic tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs sm:text-sm text-slate-400 font-medium leading-relaxed">
            {subtitle}
          </p>
        )}

        {/* Quick Search */}
        {showSearch && games.length > 6 && (
          <div className="pt-3 max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filtrar jogo por nome..."
                className="w-full bg-slate-900/80 border border-white/10 focus:border-purple-500/60 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all text-center"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Game Cards Grid - Displaying only the game name */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 w-full">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="h-24 sm:h-28 rounded-2xl bg-slate-900/40 border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/30 border border-white/5 rounded-2xl p-6 max-w-md mx-auto space-y-2">
          <p className="text-sm font-bold text-slate-300">Nenhum jogo encontrado com esse nome.</p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-xs text-purple-400 hover:underline font-bold"
          >
            Limpar busca
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 w-full">
          {filteredGames.map((game) => {
            const isSelected = selectedGameId !== undefined && selectedGameId !== 'All' && String(selectedGameId) === String(game.id);

            return (
              <button
                key={game.id}
                onClick={() => onSelectGame(game.id, game)}
                className={`group relative min-h-[96px] sm:min-h-[110px] p-4 sm:p-5 rounded-2xl border transition-all duration-200 flex items-center justify-center text-center shadow-lg active:scale-95 hover:scale-[1.03] ${
                  isSelected
                    ? 'bg-purple-950/60 border-purple-500/80 ring-2 ring-purple-500/40 text-white shadow-purple-900/30'
                    : 'bg-slate-900/60 hover:bg-purple-950/40 border-white/5 hover:border-purple-500/40 text-slate-200 hover:text-white'
                }`}
              >
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider line-clamp-2 select-none group-hover:text-white transition-colors">
                  {game.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
